interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class RequestDeduplicator {
  private pendingRequests = new Map<string, PendingRequest>();
  private readonly REQUEST_TIMEOUT = 30000;

  /**
   * Deduplicate a request
   * @param url - The URL of the request
   * @param method - The method of the request
   * @param requestFn - The function to call to make the request
   * @param data - The data of the request
   * @returns The promise of the request
   */
  async deduplicateRequest<T>(
    url: string,
    method: string,
    requestFn: () => Promise<T>,
    data?: any,
  ): Promise<T> {
    // JSON.stringify(FormData) is "{}", so two uploads would share a key.
    // The typeof guard keeps Apps Script (no FormData global) working.
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return requestFn();
    }

    const key = this.createRequestKey(url, method, data);

    this.cleanupExpiredRequests();

    // If there's already a pending request with the same key, check if it's expired
    if (this.pendingRequests.has(key)) {
      const pendingRequest = this.pendingRequests.get(key)!;
      const age = Date.now() - pendingRequest.timestamp;

      if (age < this.REQUEST_TIMEOUT) {
        return this.pendingRequests.get(key)!.promise;
      }

      this.pendingRequests.delete(key);
    }

    // Create a new request with timeout protection
    const requestPromise = this.createRequestWithTimeout(
      requestFn,
      key,
    ).finally(() => this.pendingRequests.delete(key));

    // Store the pending request
    this.pendingRequests.set(key, {
      promise: requestPromise,
      timestamp: Date.now(),
    });

    return requestPromise;
  }

  /**
   * Manually clear a specific request (useful for testing or edge cases)
   * @param url - The URL of the request
   * @param method - The method of the request
   * @param data - The data of the request
   */
  clearRequest(url: string, method: string, data?: any): void {
    const key = this.createRequestKey(url, method, data);
    this.pendingRequests.delete(key);
  }

  /**
   * Clear all pending requests
   */
  clearAllRequests(): void {
    this.pendingRequests.clear();
  }

  /**
   * Get current pending request count (useful for debugging)
   * @returns Number of pending requests
   */
  getPendingRequestCount(): number {
    return this.pendingRequests.size;
  }

  private createRequestKey(url: string, method: string, data?: any): string {
    const hasData = typeof data !== 'undefined' && data !== null;
    const dataString = hasData ? JSON.stringify(data) : '';
    return `${method.toUpperCase()}:${url}:${dataString}`;
  }

  private createRequestWithTimeout<T>(
    requestFn: () => Promise<T>,
    key: string,
  ): Promise<T> {
    // Create a timeout promise that rejects after REQUEST_TIMEOUT
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${this.REQUEST_TIMEOUT}ms`));
      }, this.REQUEST_TIMEOUT);
    });

    // Race the actual request against the timeout
    return Promise.race([requestFn(), timeoutPromise]);
  }

  private cleanupExpiredRequests(): void {
    const now = Date.now();
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.timestamp > this.REQUEST_TIMEOUT) {
        this.pendingRequests.delete(key);
      }
    }
  }
}

export const requestDeduplicator = new RequestDeduplicator();
